import { QueryClient } from "@tanstack/react-query";
import Axios from "axios";
import { toast } from "sonner";
import { getApiErrorMessage } from "./apiError";
import { QUERY_FRESHNESS } from "./queryFreshness";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: QUERY_FRESHNESS.staleTime,
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
      retry: (failureCount, error) => {
        // Don't retry cancelled requests (caused by component unmount or React Strict Mode)
        if (Axios.isCancel(error) || (error as any)?.code === "ERR_CANCELED") {
          return false;
        }

        // Don't retry on 4xx errors
        if (error instanceof Error && "response" in error) {
          const status = (error as any).response?.status;
          if (status && status >= 400 && status < 500) {
            return false;
          }
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: QUERY_FRESHNESS.refetchOnMount,
      // Only refetch if data is stale
      refetchOnReconnect: "always",
      // Keep previous data while refetching (prevents UI flickering)
      placeholderData: (previousData: unknown) => previousData,
      // Increase network mode tolerance
      networkMode: "always",
    },
    mutations: {
      retry: (failureCount, error) => {
        // Don't retry cancelled mutations
        if (Axios.isCancel(error) || (error as any)?.code === "ERR_CANCELED") {
          return false;
        }
        // Don't retry mutations on 4xx errors
        if (error instanceof Error && "response" in error) {
          const status = (error as any).response?.status;
          if (status && status >= 400 && status < 500) {
            return false;
          }
        }
        return failureCount < 2;
      },
      onError: (error) => {
        // Don't show toast for cancelled mutations
        if (Axios.isCancel(error) || (error as any)?.code === "ERR_CANCELED") {
          return;
        }

        // Global fallback for mutations that don't declare their own onError:
        // show the server's message (error / detail / field errors).
        toast.error(getApiErrorMessage(error));
      },
    },
  },
});
